import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Calendar, Tag, ArrowLeft, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

// Converte markdown minimale in HTML (titoli ##, ###, liste -, grassetto **, link, paragrafi)
function renderMarkdown(md) {
  if (!md) return "";
  // Se contiene già <tag HTML, usa com'è
  if (/<\w+[^>]*>/.test(md)) return md;
  let html = md;
  html = html.replace(/^### (.*)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.*)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.*)$/gm, "<h1>$1</h1>");
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  // Liste
  html = html.replace(/((?:^- .*(?:\n|$))+)/gm, (block) => {
    const items = block.trim().split("\n").map((l) => `<li>${l.replace(/^- /, "")}</li>`).join("");
    return `<ul>${items}</ul>`;
  });
  // Paragrafi (righe non-tag separate da linea vuota)
  html = html.split(/\n{2,}/).map((p) => {
    const trimmed = p.trim();
    if (!trimmed) return "";
    if (/^<(h\d|ul|ol|p|blockquote|pre|img)/.test(trimmed)) return trimmed;
    return `<p>${trimmed.replace(/\n/g, "<br/>")}</p>`;
  }).join("\n");
  return html;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} · ToolBadge Blog`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="blog-post-page">
      <nav className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-[#EDE5D8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="Home">
            <LogoMark size={56} />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="text-sm hover:text-[#FF7A29] flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Blog</Link>
            <Link to="/register"><Button className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] text-white">Prova gratis</Button></Link>
          </div>
        </div>
      </nav>

      {loading && <div className="text-center py-32 text-[#757575]">Caricamento articolo…</div>}

      {!loading && notFound && (
        <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="blog-not-found">
          <BookOpen className="w-16 h-16 mx-auto text-[#FF7A29]/50 mb-4"/>
          <h1 className="font-display text-4xl mb-3">Articolo non trovato</h1>
          <p className="text-[#757575] mb-6">L'articolo che stai cercando non esiste o è stato rimosso.</p>
          <Link to="/blog"><Button className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] text-white">Torna al Blog</Button></Link>
        </div>
      )}

      {!loading && post && (
        <>
          {post.cover_image_url && (
            <div className="w-full aspect-[21/9] max-h-[480px] overflow-hidden bg-[#0A0A0A]" data-testid="blog-cover">
              <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover"/>
            </div>
          )}
          <article className="max-w-[760px] mx-auto px-6 py-12 md:py-20">
            <div className="mb-6 flex items-center gap-3 text-xs text-[#757575]">
              <Link to="/blog" className="hover:text-[#FF7A29]">← Blog</Link>
              <span>·</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5"/> {fmtDate(post.published_at)}</span>
              <span>·</span>
              <span className="font-semibold">{post.author}</span>
            </div>

            <h1 className="font-display text-4xl md:text-5xl leading-tight mb-5" data-testid="blog-post-title">{post.title}</h1>
            <p className="text-xl text-[#555] mb-10 leading-relaxed">{post.excerpt}</p>

            {post.tags && post.tags.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-10">
                {post.tags.map((t) => (
                  <span key={t} className="text-xs uppercase tracking-widest bg-[#FFF0E3] text-[#E85D00] rounded-full px-3 py-1 font-semibold"><Tag className="w-3 h-3 inline mr-1"/>{t}</span>
                ))}
              </div>
            )}

            <div
              data-testid="blog-post-content"
              className="prose-blog max-w-none"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content || "") }}
            />

            <div className="mt-16 pt-10 border-t border-[#EDE5D8] text-center">
              <h3 className="font-display text-2xl mb-3">Vuoi provare Tool Badge?</h3>
              <p className="text-[#555] mb-5">Attiva il trial gratuito. Nessuna carta richiesta.</p>
              <Link to="/register"><Button className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] text-white h-12 px-8 text-base">Inizia il trial gratuito</Button></Link>
            </div>
          </article>
        </>
      )}

      <style>{`
        .prose-blog h2 { font-size: 1.75rem; font-weight: 700; margin-top: 2rem; margin-bottom: 1rem; line-height: 1.3; }
        .prose-blog h3 { font-size: 1.35rem; font-weight: 700; margin-top: 1.5rem; margin-bottom: 0.75rem; }
        .prose-blog p { font-size: 1.05rem; line-height: 1.75; margin-bottom: 1.25rem; color: #333; }
        .prose-blog ul { list-style: disc; margin-left: 1.5rem; margin-bottom: 1.25rem; }
        .prose-blog li { font-size: 1.05rem; line-height: 1.75; margin-bottom: 0.4rem; color: #333; }
        .prose-blog strong { font-weight: 700; color: #0A0A0A; }
        .prose-blog code { background: #FFF0E3; padding: 2px 6px; border-radius: 4px; font-size: 0.95em; color: #E85D00; }
      `}</style>

      <footer className="bg-[#0A0A0A] text-white/60 py-10 text-center text-sm">
        © {new Date().getFullYear()} ToolBadge · <Link to="/" className="hover:text-[#FF7A29]">Home</Link> · <Link to="/blog" className="hover:text-[#FF7A29]">Blog</Link> · <Link to="/privacy" className="hover:text-[#FF7A29]">Privacy</Link>
      </footer>
    </div>
  );
}
